import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, Card, CardContent, CircularProgress, Container, TextField, Typography, CssBaseline } from '@mui/material';

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { ThemeProvider as CustomThemeProvider, useTheme } from "../contexts/ThemeContext";
import { useSystemButtonStyles } from "../hooks/useSystemButtonStyles";
import AuthPageToggleBar from "../components/AuthPageToggleBar";
import PublicQuestionnaireForm from "../comp/anmalning/PublicQuestionnaireForm";
import {
  fetchPublicAnmalningApplyForm,
  submitPublicAnmalningApplication,
} from "../utils/api/api";
import {
  buildAnmalningPublicLink,
  getAnmalningTokenFromSearch,
} from "../utils/anmalningPublicLink";
import useAnmalningLinkLanguage from "../hooks/useAnmalningLinkLanguage";
import { resolveAnmalningApiError } from "../utils/anmalningApiErrors";
import { authCardShellSx, authFieldSx, authPageOuterSx } from "../utils/authPageStyles";
import { APP_FONT_FAMILY } from "../constants/typography";
import newEraLogo from "../NewEraLogo.png";
import { getMm } from "../theme/modernMaterialEnterprise";

const LOGO_LIGHT = `${process.env.PUBLIC_URL || ""}/NewEraBlackLogo.png`;
const FONT = APP_FONT_FAMILY;

function validateAnswers(fields, answers) {
  for (const field of fields) {
    if (field.hidden || field.type === "heading" || !field.required) continue;
    const val = answers[field.id];
    if (
      field.type === "multi_select" ||
      field.type === "photo" ||
      field.type === "consent"
    ) {
      if (!Array.isArray(val) || val.length === 0) return false;
    } else if (val == null || String(val).trim() === "") {
      return false;
    }
  }
  return true;
}

function AnmalningApplyContent() {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();
  const { primaryButtonSx, outlineButtonSx } = useSystemButtonStyles();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => getAnmalningTokenFromSearch(location.search), [location.search]);
  const linkLang = useAnmalningLinkLanguage(location.search);
  const groupId = searchParams.get("groupId") || searchParams.get("group");
  const formLang = linkLang || i18n.language;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState(null);
  const [applicantName, setApplicantName] = useState("");
  const [applicantEmail, setApplicantEmail] = useState("");
  const [applicantPhone, setApplicantPhone] = useState("");
  const [answers, setAnswers] = useState({});

  const fieldSx = useMemo(() => authFieldSx(isDarkMode), [isDarkMode]);
  const cardShellSx = useMemo(() => authCardShellSx(isDarkMode), [isDarkMode]);

  useEffect(() => {
    if (!token || !groupId) {
      setLoading(false);
      setError(t("anmalningar_apply_invalid_link"));
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchPublicAnmalningApplyForm(token, groupId, formLang);
        if (!cancelled) setFormData(data);
      } catch {
        if (!cancelled) setError(t("anmalningar_apply_load_failed"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, groupId, formLang, t]);

  const handleAnswerChange = (fieldId, value) => {
    setAnswers((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData || !token || !groupId) return;

    if (!applicantName.trim()) {
      setError(t("anmalningar_apply_name_required"));
      return;
    }
    if (!applicantEmail.trim()) {
      setError(t("anmalningar_apply_email_required"));
      return;
    }
    if (!validateAnswers(formData.questionnaire?.fields || [], answers)) {
      setError(t("anmalningar_apply_fields_required"));
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await submitPublicAnmalningApplication({
        token,
        groupId: parseInt(groupId, 10),
        applicantName: applicantName.trim(),
        applicantEmail: applicantEmail.trim(),
        applicantPhone: applicantPhone.trim(),
        answers,
      });
      setSuccess(true);
    } catch (err) {
      setError(resolveAnmalningApiError(t, err, "anmalningar_apply_submit_failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const backUrl = token
    ? buildAnmalningPublicLink({ token, lang: linkLang, relative: true })
    : "/anmalning";

  return (
    <Box sx={authPageOuterSx(isDarkMode)}>
      <CssBaseline />
      <AuthPageToggleBar />
      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 5 }, px: { xs: 2, sm: 4 } }}>
        <Button
          component={RouterLink}
          to={backUrl}
          startIcon={<ArrowBackIcon />}
          sx={{ ...outlineButtonSx, mb: 2 }}
        >
          {t("anmalningar_apply_back")}
        </Button>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : null}

        {!loading && success ? (
          <Card elevation={0} sx={cardShellSx}>
            <CardContent sx={{ textAlign: "center", py: 4 }}>
              <CheckCircleIcon sx={{ fontSize: 56, color: "#22C55E", mb: 2 }} />
              <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "1.25rem", mb: 1 }}>
                {t("anmalningar_apply_success_title")}
              </Typography>
              <Typography sx={{ color: getMm(isDarkMode).onSurfaceVariant, fontFamily: FONT }}>
                {formData?.group?.registrationMode === "interest_review"
                  ? t("anmalningar_apply_success_review")
                  : t("anmalningar_apply_success_default")}
              </Typography>
            </CardContent>
          </Card>
        ) : null}

        {!loading && !success && formData ? (
          <Card elevation={0} sx={cardShellSx}>
            <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Box component="img" src={isDarkMode ? newEraLogo : LOGO_LIGHT} alt="NewEra" sx={{ height: 36, mb: 1.5 }} />
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: "1.25rem" }}>
                  {formData.group?.groupName}
                </Typography>
                <Typography sx={{ color: getMm(isDarkMode).onSurfaceVariant, fontFamily: FONT }}>
                  {formData.clubname}
                </Typography>
              </Box>

              {formData.group?.registrationInfoBefore ? (
                <Typography
                  sx={{
                    whiteSpace: "pre-wrap",
                    mb: 3,
                    fontFamily: FONT,
                    color: getMm(isDarkMode).onSurfaceVariant,
                  }}
                >
                  {formData.group.registrationInfoBefore}
                </Typography>
              ) : null}

              {error ? (
                <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }}>
                  {error}
                </Alert>
              ) : null}

              <Box component="form" onSubmit={handleSubmit}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, fontFamily: FONT }}>
                  {t("anmalningar_apply_contact_heading")}
                </Typography>
                <TextField
                  fullWidth
                  required
                  label={t("name")}
                  value={applicantName}
                  onChange={(e) => setApplicantName(e.target.value)}
                  sx={{ ...fieldSx, mb: 2 }}
                />
                <TextField
                  fullWidth
                  label={t("email")}
                  type="email"
                  value={applicantEmail}
                  onChange={(e) => setApplicantEmail(e.target.value)}
                  sx={{ ...fieldSx, mb: 2 }}
                />
                <TextField
                  fullWidth
                  label={t("anmalningar_apply_phone")}
                  value={applicantPhone}
                  onChange={(e) => setApplicantPhone(e.target.value)}
                  sx={{ ...fieldSx, mb: 3 }}
                />

                <PublicQuestionnaireForm
                  fields={formData.questionnaire?.fields || []}
                  answers={answers}
                  onChange={handleAnswerChange}
                  isDarkMode={isDarkMode}
                  fieldSx={fieldSx}
                />

                <Button
                  type="submit"
                  fullWidth
                  disabled={submitting}
                  sx={{ ...primaryButtonSx, mt: 3, py: 1.25 }}
                >
                  {submitting ? t("submitting") : t("anmalningar_apply_submit")}
                </Button>
              </Box>
            </CardContent>
          </Card>
        ) : null}

        {!loading && !success && !formData && error ? (
          <Alert severity="error" sx={{ borderRadius: "8px" }}>
            {error}
          </Alert>
        ) : null}
      </Container>
    </Box>
  );
}

export default function AnmalningApplyPage() {
  return (
    <CustomThemeProvider>
      <AnmalningApplyContent />
    </CustomThemeProvider>
  );
}
